import React, { useState } from 'react';
import { Image, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { sourceLabels } from '../application/inventoryUtils';
import { ProminentSearchWithScanner } from '../../shared/ui/ProminentSearchWithScanner';
import {
  ProductSearchSuggestionsPanel,
  type ProductSearchSuggestionItem,
} from '../../shared/product-search/components/ProductSearchSuggestionsPanel';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalFormSection } from '../../shared/modals/ModalFormSection';
import { formatStockQuantity } from '../application/inventoryUtils';
import { addStockModalStyles as styles } from './addStockModal.styles';
import type { AddStockModalProps, RemoteHit } from './addStockModal.types';

type Props = Pick<
  AddStockModalProps,
  | 'productQuery'
  | 'selectedProduct'
  | 'entryScanFeedback'
  | 'entrySuggestion'
  | 'onProductQueryChange'
  | 'onOpenScanner'
  | 'showBarcodeCameraButton'
  | 'onProductQuerySubmitEditing'
  | 'autoFocusProductQueryOnWeb'
  | 'onOpenCatalog'
  | 'onClearSelectedProduct'
> & {
  suggestionsOpen: boolean;
  suggestionItems: ProductSearchSuggestionItem[];
  productLoadingMore: boolean;
  productHasMore: boolean;
  onLoadMoreProducts: () => void;
  onPickSuggestion: (item: ProductSearchSuggestionItem) => void;
  stockQuantity: number | null;
};

export const AddStockModalProductSection = ({
  productQuery,
  selectedProduct,
  entryScanFeedback,
  entrySuggestion,
  onProductQueryChange,
  onOpenScanner,
  showBarcodeCameraButton = true,
  onProductQuerySubmitEditing,
  autoFocusProductQueryOnWeb = false,
  onOpenCatalog,
  onClearSelectedProduct,
  suggestionsOpen,
  suggestionItems,
  productLoadingMore,
  productHasMore,
  onLoadMoreProducts,
  onPickSuggestion,
  stockQuantity,
}: Props) => {
  const suggestionFooter = (
    <ProductSearchSuggestionsPanel
      open={suggestionsOpen}
      loading={productLoadingMore && suggestionItems.length === 0}
      items={suggestionItems}
      selectedId={selectedProduct?.id ?? null}
      emptyMessage="Ningún producto físico coincide. Podés cargarlo en Catálogo."
      onPick={onPickSuggestion}
      loadMoreLabel={productHasMore ? 'Cargar más productos' : undefined}
      onLoadMore={productHasMore ? onLoadMoreProducts : undefined}
      loadingMore={productLoadingMore && suggestionItems.length > 0}
    />
  );

  if (selectedProduct) {
    return (
      <ModalFormSection title="Producto">
        <View style={styles.productCard}>
          <SelectedProductThumb imageUrl={selectedProduct.imageUrl} />
          <View style={styles.productCardBody}>
            <Text style={styles.productCardName} numberOfLines={2}>
              {selectedProduct.name}
            </Text>
            <Text style={styles.productCardMeta} numberOfLines={1}>
              {selectedProduct.sku || 'Sin SKU'}
              {stockQuantity != null
                ? ` · Stock: ${formatStockQuantity(stockQuantity, selectedProduct.unitOfMeasure)}`
                : ''}
            </Text>
          </View>
          <TouchableOpacity
            onPress={onClearSelectedProduct}
            accessibilityRole="button"
            accessibilityLabel="Cambiar producto"
            style={styles.productCardChange}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={styles.productCardChangeText}>Cambiar</Text>
          </TouchableOpacity>
        </View>
        {entryScanFeedback ? (
          <Text style={[posUi.linkText, styles.entryScanFeedback]} numberOfLines={2}>
            {entryScanFeedback}
          </Text>
        ) : null}
        {entrySuggestion ? (
          <RemoteLookupSuggestionCard entrySuggestion={entrySuggestion} onOpenCatalog={onOpenCatalog} />
        ) : null}
      </ModalFormSection>
    );
  }

  return (
    <ModalFormSection title="Producto">
      <ModalFormField label="Buscar producto">
        <ProminentSearchWithScanner
          sectionLabel={null}
          unifiedField
          scanTone="neutral"
          value={productQuery}
          onChangeText={(text) => {
            onProductQueryChange(text);
          }}
          onPressScan={onOpenScanner}
          showBarcodeCameraButton={showBarcodeCameraButton}
          placeholder="Nombre, SKU o código…"
          placeholderCompact="Producto…"
          accessibilityLabel="Buscar producto para ingreso de stock"
          onSubmitEditing={onProductQuerySubmitEditing}
          autoFocusOnWeb={false}
          footer={suggestionFooter}
        />
        {entryScanFeedback ? (
          <Text style={[posUi.linkText, styles.entryScanFeedback]} numberOfLines={2}>
            {entryScanFeedback}
          </Text>
        ) : null}
        {entrySuggestion ? (
          <RemoteLookupSuggestionCard entrySuggestion={entrySuggestion} onOpenCatalog={onOpenCatalog} />
        ) : null}
      </ModalFormField>
    </ModalFormSection>
  );
};

/** Thumb 36px del producto elegido — mismo placeholder que las sugerencias. */
const SelectedProductThumb = ({ imageUrl }: { imageUrl?: string | null }) => {
  const [failed, setFailed] = useState(false);
  const show = Boolean(imageUrl) && !failed;
  return (
    <View style={styles.productThumb}>
      {show ? (
        <Image
          source={{ uri: imageUrl! }}
          style={styles.productThumbImage}
          resizeMode="contain"
          onError={() => setFailed(true)}
        />
      ) : (
        <Ionicons name="cube-outline" size={16} color={ticketColors.ink3} />
      )}
    </View>
  );
};

const RemoteLookupSuggestionCard = ({
  entrySuggestion,
  onOpenCatalog,
}: {
  entrySuggestion: RemoteHit;
  onOpenCatalog: () => void;
}) => (
  <View style={[posUi.paymentCard, styles.lookupAccent]}>
    <View style={styles.lookupHeader}>
      <Text style={[typography.body, { color: ticketColors.ink, fontWeight: '700' }]} numberOfLines={2}>
        {entrySuggestion.suggestion.name}
      </Text>
      <Text style={styles.lookupBadge}>{sourceLabels[entrySuggestion.source]}</Text>
    </View>
    <Text style={posUi.checkoutHint}>
      Agregalo en Catálogo y volvé a escanear para ingresar stock.
    </Text>
    <TouchableOpacity style={[posUi.primaryButton, styles.lookupCta]} onPress={onOpenCatalog}>
      <Text style={posUi.primaryButtonText}>Abrir Catálogo</Text>
    </TouchableOpacity>
  </View>
);
